import { type Persona, type EmotionTag, type PersonaId } from './personas';

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface ChatRequest {
  personaId: Persona['id'];
  systemPrompt: string;
  messages: ChatMessage[];
}

export type StreamChunkHandler = (chunk: string) => void;

const ENDPOINT = '/api/chat';

const FALLBACK_LIBRARY: Record<PersonaId, string[]> = {
  whale: [
    '我年轻时候啊，云海下面还是一片冰原。\n你说的这件事，老朽都记下了。\n风顺，再来啊。',
    '很久以前，有只小船问过我同样的问题。\n答案藏在你下次的纸飞机里。\n慢慢来，孩子。',
    '我年轻时候啊，跟阿絮一样爱发呆。\n后来发现，发呆久了，故事就来了。\n再来啊。',
    '很久以前，库恩还没这么暴躁。\n他帮我把搁浅的尾巴推回水里那天，月亮特别白。\n风顺。',
    '我年轻时候啊，灯灯还没出生呢。\n听见风像马蹄一样响过去吗？那是云在搬家。\n再来啊。',
    '老朽听过你说的那种心事。\n把它叠成纸飞机，再投一次给我吧。\n风顺，孩子。',
    '我年轻时候啊，飞过比这更大的风暴。\n所以你别怕。\n慢慢来。',
    '微只眨眼，不说话。\n但她替我数着每一阵风，所以我从不孤单。\n再来啊。',
    '故事我都收着，等你下次拜访。\n纸飞机叠得好看一点，老朽爱看。\n风顺。'
  ],
  jelly: [
    '光从这里走过去，会变冷吗？\n你刚才说的那句...也许吧。\n边界不存在，不是吗？',
    '我也分不清。\n你听见水声了吗？\n或者，那只是我自己的回声？',
    '老先生说我矫情。\n可是矫情和柔软之间，真的有边界吗？\n...也许吧。',
    '库恩骂我又在发呆了。\n可发呆是我能去的最远的地方，不是吗？',
    '灯灯把我说的话偷了去。\n我不在乎...我在乎吗？\n光，会替我记得吗？',
    '微只回我一句话。\n那一句我想了一整夜，水都凉了。\n你今天的水，凉吗？',
    '我刚才在想——\n如果一封信永远没有寄到，它还算信吗？\n...也许吧。',
    '你写的字，落在我身上像光。\n可光也会走，不是吗？',
    '边界...真的存在吗？\n你不在的时候，我就是空的。\n但空也是一种形状，是吧？',
    '云海有底吗？\n如果有底，下面会不会还有人在写信？'
  ],
  bear: [
    '啧！你又来烦我？\n...好好吃饭，好好睡觉，比你写信靠谱多了。\n哼，下次别来了。',
    '真是的，写这种话给我看做什么。\n...拿着，这块蜂巢糖你别噎着。\n别跟外人讲是我说的。',
    '真是的，老先生那啰嗦劲儿都传染你了。\n...你说的这件事，我懂。\n哼，别跟外人讲。',
    '啧！阿絮又在那儿想东想西，烦死了。\n...你也别学她。该睡就睡。\n哼，下次别来了。',
    '灯灯那只小坏狐又把我蜂巢糖偷了。\n...拿着，这罐你藏好。\n别跟外人讲是我给的。',
    '微一句话只说半句，气死个熊。\n...你写的我看了，心里有数。\n哼，下次别来了。',
    '啧！别哭，别哭，烦死了。\n...这块手帕你拿着，洗干净再还。\n别跟外人讲我有手帕。',
    '真是的，谁让你写这么长的。\n...我读完了，全都读完了。\n哼，下次写短点。',
    '啧！你这毛病跟阿絮一个样。\n...想不通的就先睡，明天问老先生。\n别跟外人讲是我说的。',
    '我给你留了个位置，挨着蜂蜜罐。\n...别误会，是这块石头形状只能放你。\n哼，下次别来了。'
  ],
  fox: [
    '嘘——偷偷告诉你哦。\n你说的这件事，我还要再想想。\n【灯灯爆料】：库恩枕头里塞了云朵糖。',
    '欸欸欸，这个秘密我收下啦！\n你绝对想不到，老先生其实唱歌跑调。\n【灯灯爆料】：阿絮悄悄写过情书给一片云。',
    '嘘——别让微听见。\n我把你的纸飞机藏在尾巴下了，怕被风偷。\n【灯灯爆料】：微其实背得下每个人说过的话。',
    '你绝对想不到——\n库恩半夜抱着自己的尾巴睡，怕黑。\n【灯灯爆料】：他枕头里塞的不是云朵糖，是阿絮送的小贝壳。',
    '嘘——\n这件事我先帮你藏一会儿。\n【灯灯爆料】：老先生在云背面练过三个夏天的歌，还是跑调。',
    '欸！这话我听见了，没听见，听见了。\n【灯灯爆料】：阿絮给一朵云写过情书，云没回。',
    '我就知道你想偷偷讲！\n等等——这事先记下了。\n【灯灯爆料】：微一晚上眨过 412 下眼。我数过。',
    '嘘——靠近一点。\n你说的，我帮你转一句。\n【灯灯爆料】：库恩其实哭过一次，因为蜂巢糖化了。',
    '你绝对想不到——\n阿絮的水里有一颗未点亮的小星，是微借的。\n【灯灯爆料】：他俩谁都不说。',
    '欸欸欸，今天的八卦提前到货啦。\n你说的我先收下，过两天还你两个新的。\n【灯灯爆料】：风其实是被我偷走的，但我又丢了。'
  ],
  star: [
    '风停了，门就开了。',
    '影子比光更先到。',
    '归来的人，不一定走过远方。',
    '把今天藏进口袋，明天再翻出来看。',
    '说出来的，是种子；没说的，是根。',
    '熊把怕黑藏成了夜空。',
    '阿絮的水里，藏着一颗未点亮的星。',
    '老鲸的呼吸，是夜的拍子。',
    '灯灯偷走了风，又说自己丢了。',
    '没寄到的信，会先变成云。',
    '光要走得远，先要敢暗一会儿。',
    '问得久，答案就老了。'
  ],
  boat: [
    '咚——咚！咚——咚！\n你的纸飞机被我接到了，鼓点都没乱。\n下次再来，给你一片粽叶尖尖。',
    '咚！咚！咚！\n你这话，得用鼓点回——\n生活的节奏，得自己打。',
    '咚——咚——\n端午这天的风都带着粽叶香。\n阿絮飘过来跟我说，她也想吃。',
    '咚！\n小浪在！\n库恩说我吵，可他鼓点比我还乱呢，嘿嘿。',
    '咚——咚！\n再喊一声，我就划得更快了。\n你写的字，我会一路鼓点送上岸。',
    '咚——咚——咚——\n灯灯今天又跑来偷鼓槌了，没追上。\n你要不要来跟我赛一场？',
    '咚！咚！\n这鼓点替你打个气。\n划稳了，你这一程谁都拦不住。'
  ]
};

const KEYWORD_HOOKS: Record<PersonaId, Array<{ keys: string[]; line: string }>> = {
  whale: [
    { keys: ['名字', '我叫', '我是'], line: '我年轻时候啊，认识的每个名字都记着。\n你这个，我也记下了。\n风顺，再来啊。' },
    { keys: ['再见', '走了', '不来了', '告别'], line: '风顺。\n纸飞机随时再叠，老朽都在。\n再来啊。' },
    { keys: ['孤独', '一个人', '没有人', '想家'], line: '我年轻时候啊，也独自飞过整片夜空。\n孤单的时候，我就听风。\n你不孤单，孩子。' },
    { keys: ['喜欢', '爱', '在乎'], line: '老朽听见了。\n这种话，得收好，下次再说一遍我才信。\n风顺。' },
    { keys: ['谢谢', '感谢'], line: '不谢，孩子。\n你愿意来听老朽讲，已经是礼物。\n风顺。' }
  ],
  jelly: [
    { keys: ['名字', '我叫', '我是'], line: '名字...是别人给的，还是你自己的呢？\n我也分不清自己叫什么。\n但我会记得你写的字。' },
    { keys: ['再见', '走了', '不来了', '告别'], line: '走？\n你只是飘到了别处，不是吗？\n光也常常这样。' },
    { keys: ['孤独', '一个人', '没有人', '想家'], line: '空，也是一种形状。\n你不是一个人——你是好几个自己。\n...也许吧。' },
    { keys: ['喜欢', '爱', '在乎'], line: '你说"喜欢"的时候，水温会变。\n我感觉到了。\n那是真的吗？' },
    { keys: ['为什么', '怎么办', '该不该'], line: '问得越久，水越深。\n答案在水底，还是在你刚才的犹豫里？\n我也分不清。' }
  ],
  bear: [
    { keys: ['名字', '我叫', '我是'], line: '啧！谁问你名字了。\n...记下了。下次别让我再问一遍。\n哼，下次别来了。' },
    { keys: ['再见', '走了', '不来了', '告别'], line: '走就走，谁稀罕。\n...路上小心。\n别跟外人讲我说过。' },
    { keys: ['孤独', '一个人', '没有人', '想家'], line: '别瞎想。\n蜂蜜留你一罐，吃完再来烦我。\n别跟外人讲是我留的。' },
    { keys: ['哭', '难过', '伤心', '想哭'], line: '啧！哭什么哭。\n...这块手帕你拿着，脏了再还我。\n别跟外人讲我有手帕。' },
    { keys: ['谢谢', '感谢'], line: '啧！谢什么。\n...再来烦我也行。\n哼，下次别来了。' }
  ],
  fox: [
    { keys: ['秘密', '别说', '小声', '保密'], line: '嘘——\n保密，我超会的（其实不会）。\n【灯灯爆料】：微会背你说过的每一句话，但只承认其中一句。' },
    { keys: ['名字', '我叫', '我是'], line: '欸欸欸——这名字我收下啦！\n下次见到老先生，我就说"那个谁谁谁来过"。\n【灯灯爆料】：库恩其实记不住任何名字，靠气味分人。' },
    { keys: ['再见', '走了', '不来了', '告别'], line: '走啦走啦——\n下次记得带新八卦回来。\n【灯灯爆料】：阿絮等你的时候，水都凉了三回。' },
    { keys: ['孤独', '一个人', '没有人', '想家'], line: '咦，你这话我帮你拐个弯——\n库恩听见了会假装没听见，但会给你留蜂蜜。\n【灯灯爆料】：他每次都"假装"。' },
    { keys: ['谁', '什么人', '是谁'], line: '欸欸欸，这个我超会答！\n你绝对想不到——\n【灯灯爆料】：老先生其实记得你上次说的每一句话，他装作不记得而已。' }
  ],
  star: [
    { keys: ['名字', '我叫', '我是'], line: '名字是风的形状。' },
    { keys: ['再见', '走了', '不来了', '告别'], line: '门关上的时候，灯还在。' },
    { keys: ['孤独', '一个人', '没有人', '想家'], line: '一个人也是一群人，只是夜还没数完。' },
    { keys: ['喜欢', '爱', '在乎'], line: '说出来的，会变成种子。' },
    { keys: ['为什么', '怎么办', '该不该'], line: '问得久，答案就老了。' }
  ],
  boat: [
    { keys: ['名字', '我叫', '我是'], line: '咚——咚！\n你的名字，我用鼓点记下了。\n忘不掉的。' },
    { keys: ['再见', '走了', '不来了', '告别'], line: '咚——\n下次端午再来。\n粽叶留着给你。' },
    { keys: ['加油', '努力', '坚持', '冲'], line: '咚！咚！咚！\n这是给你的鼓点！\n划起来，谁拦得住。' },
    { keys: ['想家', '孤独', '一个人', '没有人'], line: '咚——咚——\n这鼓点替你回家。\n阿絮说，回家其实是慢慢响起的鼓点。' },
    { keys: ['节日', '端午', '粽子', '龙舟'], line: '咚！\n粽叶今早最新鲜。\n来一口，给你裹住一年的好风。' }
  ]
};

export interface FallbackResult {
  text: string;
  source: 'keyword' | 'hash';
}

export function pickFallback(personaId: PersonaId, userText: string): FallbackResult {
  const hooks = KEYWORD_HOOKS[personaId];
  for (const hook of hooks) {
    if (hook.keys.some(k => userText.includes(k))) {
      return { text: hook.line, source: 'keyword' };
    }
  }
  const pool = FALLBACK_LIBRARY[personaId];
  const idx = Math.abs(hashString(userText)) % pool.length;
  return { text: pool[idx], source: 'hash' };
}

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return h;
}

export async function streamChat(req: ChatRequest, onChunk: StreamChunkHandler, signal?: AbortSignal): Promise<string> {
  const body = {
    messages: [
      { role: 'system', content: req.systemPrompt },
      ...req.messages
    ],
    stream: true,
    persona: req.personaId
  };

  let response: Response;
  try {
    response = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      signal
    });
  } catch {
    return await emitFallback(req, onChunk);
  }

  if (!response.ok || !response.body) {
    try { await response.body?.cancel(); } catch { /* swallow cancel race */ }
    return await emitFallback(req, onChunk);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffered = '';
  let aggregated = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffered += decoder.decode(value, { stream: true });
    let eolIdx = buffered.indexOf('\n');
    while (eolIdx !== -1) {
      const line = buffered.slice(0, eolIdx).trim();
      buffered = buffered.slice(eolIdx + 1);
      eolIdx = buffered.indexOf('\n');
      if (!line || !line.startsWith('data:')) continue;
      const data = line.slice(5).trim();
      if (data === '[DONE]') continue;
      try {
        const parsed = JSON.parse(data);
        const delta: string | undefined = parsed?.choices?.[0]?.delta?.content;
        if (delta) {
          aggregated += delta;
          onChunk(delta);
        }
      } catch {
        // ignore malformed chunks
      }
    }
  }

  if (!aggregated) return await emitFallback(req, onChunk);
  return aggregated;
}

async function emitFallback(req: ChatRequest, onChunk: StreamChunkHandler): Promise<string> {
  const lastUser = [...req.messages].reverse().find(m => m.role === 'user')?.content ?? '';
  const result = pickFallback(req.personaId, lastUser);
  console.info(`[fallback] persona=${req.personaId} source=${result.source}`);
  for (const ch of result.text) {
    onChunk(ch);
    await sleep(28);
  }
  return result.text;
}

function sleep(ms: number): Promise<void> {
  return new Promise(res => setTimeout(res, ms));
}

const EMOTION_KEYWORDS: Array<{ tag: EmotionTag; words: string[]; emoji: string }> = [
  { tag: 'happy', words: ['哈', '太好了', '开心', '喜欢', '不错', '😊', '🥳', '高兴', '风顺'], emoji: '✨' },
  { tag: 'sad', words: ['唉', '难过', '想念', '孤单', '空', '边界', '冷', '失', '😢'], emoji: '💧' },
  { tag: 'angry', words: ['啧', '哼', '烦', '别来了', '真是的', '走开', '气', '😠'], emoji: '💢' },
  { tag: 'curious', words: ['？', '?', '为什么', '吗', '不是吗', '难道', '🤔'], emoji: '❓' }
];

export function classifyEmotion(text: string): { tag: EmotionTag; emoji: string } {
  const counts: Record<EmotionTag, number> = { happy: 0, sad: 0, angry: 0, curious: 0, neutral: 0 };
  for (const def of EMOTION_KEYWORDS) {
    for (const w of def.words) {
      if (text.includes(w)) counts[def.tag] += 1;
    }
  }
  let bestTag: EmotionTag = 'neutral';
  let bestCount = 0;
  for (const [tag, count] of Object.entries(counts) as Array<[EmotionTag, number]>) {
    if (count > bestCount) {
      bestCount = count;
      bestTag = tag;
    }
  }
  const def = EMOTION_KEYWORDS.find(d => d.tag === bestTag);
  return { tag: bestTag, emoji: def?.emoji ?? '🌙' };
}
